import { applySnapshot, flow, types } from 'mobx-state-tree'
import { deleteReportFileReq } from '../../network/deleteReportFileReq'
import { downloadReportFileReq } from '../../network/downloadReportFileReq'
import { extendAppSessionAPI } from '../../network/extendAppSession'
import { extendUserSessionAPI } from '../../network/extendUserSession'
import { getTxnsRefundsDownloadListReq } from '../../network/getTxnRefundsDownloadList'
import {
  downloadFile,
  FILE_DOWNLOAD_INPROCESS,
  FILE_DOWNLOADED,
  fromDateTime,
  isOsMobile,
  SESSION_EXPIRED,
  toDateTime,
} from '../../util/util'
import { paginationSettingsWithDefaultPage1 } from '../static/data'
import { customError } from '../static/strings'
import { RootStore } from '../store'
import { Pagination } from './Transaction'

const Report = types.model('Report', {
  // srno: types.optional(types.string, ''),
  requestFrom: types.optional(types.string, ''),
  requestTo: types.optional(types.string, ''),
  requestTime: types.optional(types.string, ''),
  status: types.optional(types.string, ''),
  downloadDelete: types.optional(types.string, ''),
  fileName: types.optional(types.string, ''),
  requestId: types.optional(types.string, ''),
})

const Reports = types
  .model('Report', {
    reports: types.optional(types.array(Report), []),
    pagination: Pagination,
    message: types.optional(types.string, ''),
  })
  .actions((self) => ({
    fetchDownloadsListApiList: flow(function* fetchDownloadsListApiList(
      page: number,
      transactionType: string,
      mid: string,
    ) {
      let response
      const own = self
      try {
        const toDate = toDateTime(RootStore.settings.metaData.selectedDate.toDate ?? new Date(Date.now()))
        const fromDate = fromDateTime(RootStore.settings.metaData.selectedDate.fromDate)
        const enDate = +toDate > Date.now() ? Date.now() - 300000 : toDate // 5 min before txn
        const isSmid = RootStore.user.isSmid
        response = yield getTxnsRefundsDownloadListReq(
          RootStore.user.session.getTokens,
          mid,
          fromDate,
          `${enDate}`,
          transactionType,
          page,
          isSmid,
        )
        const parsedData = JSON.parse(JSON.stringify(response?.reports))
        own.pagination = JSON.parse(JSON.stringify(response?.page))
        // Direct assignment destroys existing Report nodes; reactions observing them
        // then fire on dead nodes. Update in-place: patch matches, remove stale, add new.
        const newIds = new Set(parsedData.map((d: any) => d.requestId))
        own.reports.filter((r) => !newIds.has(r.requestId)).forEach((r) => own.reports.remove(r))
        parsedData.forEach((newItem: any) => {
          const existing = own.reports.find((r) => r.requestId === newItem.requestId)
          if (existing) {
            applySnapshot(existing, newItem)
          } else {
            own.reports.push(newItem)
          }
        })
      } catch (e) {
        if (e?.data?.error?.code !== '332' && e?.data?.error?.code !== 'MSM00001') {
          RootStore?.response?.setToastType('ACTION_VIEW')
          RootStore?.response?.setMessage('Reports', e?.data?.error?.message ?? customError.errorOccured)
        }
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
      return response
    }),
    deleteReportFile: flow(function* deleteReportFile(requestId: string, transactionType: string) {
      let response
      try {
        response = yield deleteReportFileReq(
          requestId,
          transactionType,
          RootStore.user.selectedMid,
          RootStore.user.session.getTokens,
          RootStore.user.isSmid,
        )
        console.log('deleteReportFile response', response)
        if (response?.data?.status === 'SUCCESS') {
          if (
            self.pagination.totalPages === self.pagination.number &&
            self.pagination.number > 1 &&
            self.pagination.totalElements % 10 === 1
          ) {
            self.pagination.number = self.pagination.number - 1
            return false
          }
          return true
        } else {
          throw response
        }
      } catch (e) {
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
    }),
    downloadReportFile: flow(function* downloadReportFile(
      requestId: string,
      transactionType: string,
      filename: string,
    ) {
      let response
      try {
        response = yield downloadReportFileReq(
          requestId,
          transactionType,
          RootStore.user.selectedMid,
          RootStore.user.session.getTokens,
          RootStore.user.isSmid,
          filename,
        )
        console.log('downloadReportFile response', response)
        if (!isOsMobile()) {
          const contentType = response.headers.get('content-type')
          if (contentType === 'application/json' || !contentType) {
            response = yield response.json()
            if (response?.status === 'SUCCESS' && response?.data?.status === 'INPROCESS') {
              RootStore.response.setMessage('Your report will be available in some time')
              RootStore.response.setToastType('POSITIVE')
            } else if (response?.status === 'SUCCESS' && response?.data?.status === 'FAILED') {
              RootStore?.response?.setMessage('Export file failed')
            } else if (response.status === 'FAILURE' && response.error.code === '33306') {
              yield extendAppSessionAPI()
              yield extendUserSessionAPI()
              return (self as any).downloadReportFile(requestId, transactionType, filename)
            } else if (response.status === 'FAILURE' && response.error.code === '33607') {
              yield extendUserSessionAPI()
              return (self as any).downloadReportFile(requestId, transactionType, filename)
            } else {
              RootStore?.response?.setMessage(response.error?.message ?? customError.errorOccured)
            }
          } else {
            downloadFile(response, filename)
          }
        } else {
          if (response?.code === FILE_DOWNLOADED) {
            return response
          } else if (response?.code === FILE_DOWNLOAD_INPROCESS) {
            RootStore.response.setMessage('Report', 'Your report will be available in some time')
            RootStore.response.setToastType('ACTION_VIEW')
          } else if (response?.code === SESSION_EXPIRED) {
            extendAppSessionAPI().then(() => {
              extendUserSessionAPI().then((resp) => {
                console.log('extendUserSessionAPI response', resp)
                return (self as any).downloadReportFile(requestId, transactionType, filename)
              })
            })
          } else {
            // RootStore.response.setMessage('Report', 'Error while exporting the file')
            // RootStore.response.setToastType('ACTION_VIEW')
          }
        }
      } catch (e) {
        RootStore?.response?.setMessage(e?.message ?? customError.errorOccured)
      }
      return response
    }),
    updatePagination(selectedPage: number): void {
      self.pagination.number = selectedPage
    },
    resetPagination(): void {
      self.pagination = JSON.parse(JSON.stringify(paginationSettingsWithDefaultPage1))
    },
    setMessage(value: string) {
      self.message = value?.toString() ?? ''
    },
    resetMessage() {
      self.message = ''
    },
    setPagination(pagination: any) {
      self.pagination = pagination
    },
  }))
  .views((self) => ({
    getDownloadReportData() {
      //console.log('apptest getDownloadReportData', JSON.parse(JSON.stringify(self.reports)))
      return [...self.reports]
    },
    getMessage(): any {
      return self?.message
    },
  }))

const ReportsData = Reports.create({
  reports: [],
  pagination: paginationSettingsWithDefaultPage1,
})

export { Report, Reports, ReportsData }
